/**
 * CursorStore - Transient State Management (REQ-ARCH-01)
 * 
 * This is a vanilla JavaScript store that holds cursor state outside of React.
 * The VirtualCursor reads from this store in a requestAnimationFrame loop,
 * allowing 60fps cursor updates without triggering React re-renders.
 * 
 * @requirements REQ-ARCH-01: Cursor position (x, y) never triggers React re-render
 */

export interface CursorState {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  dwellProgress: number;
  dwellTarget: HTMLElement | null;
  isActive: boolean;
  lastUpdate: number;
}

type CursorListener = (state: CursorState) => void;

class CursorStoreClass {
  private state: CursorState;
  private listeners: Set<CursorListener> = new Set();

  constructor() {
    this.state = {
      x: typeof window !== 'undefined' ? window.innerWidth / 2 : 500,
      y: typeof window !== 'undefined' ? window.innerHeight / 2 : 400,
      velocityX: 0,
      velocityY: 0,
      dwellProgress: 0,
      dwellTarget: null,
      isActive: false,
      lastUpdate: Date.now(),
    };
  }

  /**
   * Get current state (read-only snapshot)
   */
  getState(): Readonly<CursorState> {
    return this.state;
  }

  /**
   * Update cursor position - called from tracking engines
   * This does NOT trigger React re-renders
   */
  setPosition(x: number, y: number): void {
    const now = Date.now();
    const dt = (now - this.state.lastUpdate) / 1000;
    
    // Calculate velocity for physics
    if (dt > 0 && dt < 0.5) {
      this.state.velocityX = (x - this.state.x) / dt;
      this.state.velocityY = (y - this.state.y) / dt;
    }

    this.state.x = x;
    this.state.y = y;
    this.state.lastUpdate = now;
  }

  /**
   * Update dwell progress
   */
  setDwellProgress(progress: number): void {
    this.state.dwellProgress = Math.max(0, Math.min(1, progress));
  }

  /**
   * Set dwell target element
   */
  setDwellTarget(target: HTMLElement | null): void {
    this.state.dwellTarget = target;
  }

  /**
   * Set active state
   */
  setActive(isActive: boolean): void {
    this.state.isActive = isActive;
    this.notifyListeners();
  }

  /**
   * Subscribe to state changes (for React integration if needed)
   */
  subscribe(listener: CursorListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners (used sparingly for mode changes)
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state));
  }

  /**
   * Reset to center
   */
  reset(): void {
    this.state.x = window.innerWidth / 2;
    this.state.y = window.innerHeight / 2;
    this.state.velocityX = 0;
    this.state.velocityY = 0;
    this.state.dwellProgress = 0;
    this.state.dwellTarget = null;
  }
}

// Singleton instance
export const CursorStore = new CursorStoreClass();

// Export type for external usage
export type { CursorStoreClass };
